import { type CSSProperties, Suspense, lazy, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { closeIntercom } from 'entities/Intercom'
import { onCloseAgentPanelRequested } from 'entities/WebMcp/lib/agentPanelSignal'
import { useAiAgentEnabled } from 'features/AiAgent/lib/flag'
import { useAgentBadge } from 'features/AiAgent/lib/useAgentBadge'

// Lazy so the Anthropic SDK + loop + design system are not in the default bundle.
const ChatPanel = lazy(() => import('features/AiAgent/ui/ChatPanel'))

// This launcher is statically imported into RootLayout, so it stays design-system
// free — its styling is inline (no @salesmessage/ui import in the entry path).
// The values below mirror the DS tokens the design uses (`ai-bg`, `ai`,
// `foreground`); they are literals here only because the token stylesheet ships
// inside the lazy panel chunk and is not loaded until the panel opens.
const LAUNCHER_STYLE: CSSProperties = {
  position: 'fixed',
  // Sit just left of Intercom's bottom-right launcher (right:20 + 48px wide + gap).
  right: 80,
  bottom: 20,
  zIndex: 'var(--zIndex-AI_AGENT)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  borderRadius: 999,
  padding: '12px 16px',
  background: '#F9ECFE',
  color: '#121517',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1,
  border: 'none',
  cursor: 'pointer',
}

// The DS `MessageAi` glyph, inlined for the same reason the styles are: importing
// it from @salesmessage/ui would pull the library into the entry bundle. Keep in
// step with src/components/icons/message-ai.tsx in the design system.
const MESSAGE_AI_PATH =
  'M8.33333 0C3.73096 0 0 3.73096 0 8.33333C0 9.44027 0.216268 10.4988 0.60963 11.4673C0.645161 11.5548 0.665598 11.6053 0.679604 11.6426C0.680507 11.645 0.68118 11.6484 0.681798 11.6516C0.67763 11.6823 0.672268 11.7308 0.658929 11.8108L0.164777 14.7757L0.160784 14.7996C0.138432 14.9335 0.113423 15.0832 0.103606 15.2133C0.0929014 15.3551 0.0891742 15.5807 0.191441 15.8192C0.317864 16.1139 0.552747 16.3488 0.847501 16.4752C1.08593 16.5775 1.31155 16.5738 1.45337 16.5631C1.58344 16.5532 1.73321 16.5282 1.86707 16.5059L4.85587 16.0077C4.93591 15.9944 4.98179 15.987 5.01254 15.9828L5.02406 15.9871C5.06135 16.0011 5.11186 16.0215 5.19934 16.057C6.16791 16.4504 7.2264 16.6667 8.33333 16.6667C12.9357 16.6667 16.6667 12.9357 16.6667 8.33333C16.6667 3.73096 12.9357 0 8.33333 0ZM8.33333 3.33333C8.52141 3.33333 8.69006 3.44916 8.75758 3.62471L9.67211 6.00247C9.80865 6.35749 9.85156 6.4598 9.91025 6.54234C9.96914 6.62516 10.0415 6.69752 10.1243 6.75641C10.2069 6.81511 10.3092 6.85801 10.6642 6.99456L13.042 7.90909C13.2175 7.9766 13.3333 8.14526 13.3333 8.33333C13.3333 8.52141 13.2175 8.69006 13.042 8.75758L10.6642 9.67211C10.3092 9.80865 10.2069 9.85156 10.1243 9.91025C10.0415 9.96914 9.96914 10.0415 9.91025 10.1243C9.85156 10.2069 9.80865 10.3092 9.67211 10.6642L8.75758 13.042C8.69007 13.2175 8.52141 13.3333 8.33333 13.3333C8.14526 13.3333 7.9766 13.2175 7.90909 13.042L6.99456 10.6642C6.85801 10.3092 6.81511 10.2069 6.75641 10.1243C6.69752 10.0415 6.62516 9.96914 6.54234 9.91025C6.4598 9.85156 6.35749 9.80865 6.00247 9.67211L3.62471 8.75758C3.44916 8.69006 3.33333 8.52141 3.33333 8.33333C3.33333 8.14526 3.44916 7.9766 3.62471 7.90909L6.00247 6.99456C6.35749 6.85801 6.4598 6.81511 6.54234 6.75641C6.62516 6.69752 6.69752 6.62516 6.75641 6.54234C6.81511 6.4598 6.85801 6.35749 6.99456 6.00247L7.90908 3.62471C7.9766 3.44916 8.14525 3.33333 8.33333 3.33333Z'

const MessageAiGlyph = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
    aria-hidden='true'
    focusable='false'
    style={{ flexShrink: 0 }}
  >
    <g transform='translate(1.66667 1.66667)'>
      <path fillRule='evenodd' clipRule='evenodd' d={MESSAGE_AI_PATH} fill='#C22AF4' />
    </g>
  </svg>
)

// A small dot on the launcher meaning "the agent has something for you" — an
// incomplete onboarding step. Inline-styled to keep the statically-imported
// launcher design-system free.
const BADGE_STYLE: CSSProperties = {
  position: 'absolute',
  top: -2,
  right: -2,
  width: 10,
  height: 10,
  borderRadius: 999,
  background: '#F5455C',
  border: '2px solid #fff',
}

export const AiAgentLauncher = observer(() => {
  const enabled = useAiAgentEnabled()
  const { authStore } = useEntitiesContext()
  const [open, setOpen] = useState(false)
  // Once opened, keep the panel mounted (just hidden) so the chat survives close/reopen.
  const [hasOpened, setHasOpened] = useState(false)
  const active = enabled && authStore.isLoggedIn
  const hasSomething = useAgentBadge(active)

  // The support messenger opens below this panel, so opening it closes the panel.
  useEffect(() => onCloseAgentPanelRequested(() => setOpen(false)), [])

  // Hide until the user is logged in (no agent on auth pages; tools need a user token).
  if (!active) return null

  const toggle = () => {
    const next = !open

    // This panel covers the messenger, so it cannot be left open behind it.
    if (next) closeIntercom()

    setOpen(next)
    setHasOpened(true)
  }

  return (
    <>
      {hasOpened && (
        <Suspense fallback={null}>
          <ChatPanel open={open} />
        </Suspense>
      )}
      <button type='button' style={LAUNCHER_STYLE} onClick={toggle} aria-expanded={open}>
        <MessageAiGlyph />
        Ask AI
        {hasSomething && !open && <span style={BADGE_STYLE} aria-hidden='true' />}
      </button>
    </>
  )
})
